// ENG-21348 Pure detection + pretty-print helpers for MXmlJsonViewer.
// No third-party deps — native DOMParser / XMLSerializer / JSON only.

export type XmlJsonFormat = "xml" | "json" | "text";

function isValidXml(xml: string): boolean {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  return doc.getElementsByTagName("parsererror").length === 0;
}

/**
 * Detect whether a raw string is JSON, XML, or neither.
 * JSON must open with `{`/`[` and parse; XML must open with `<` and parse without error.
 * Anything else (including malformed input) is treated as plain `text`.
 */
export function detectFormat(content: string): XmlJsonFormat {
  const trimmed = content.trim();
  if (!trimmed) return "text";

  if (trimmed[0] === "{" || trimmed[0] === "[") {
    try {
      JSON.parse(trimmed);
      return "json";
    } catch {
      // not valid JSON — fall through
    }
  }

  if (trimmed[0] === "<" && isValidXml(trimmed)) {
    return "xml";
  }

  return "text";
}

function formatJson(content: string): string {
  return JSON.stringify(JSON.parse(content), null, 2);
}

/** Re-indent a serialized XML string with two-space nesting, one node per line. */
function indentXml(xml: string): string {
  const PADDING = "  ";
  const withBreaks = xml.replace(/(>)(<)(\/*)/g, "$1\n$2$3");
  let pad = 0;
  return withBreaks
    .split("\n")
    .map((node) => {
      let indent = 0;
      if (/.+<\/\w[^>]*>$/.test(node)) {
        // open + close on the same line — no depth change
        indent = 0;
      } else if (/^<\/\w/.test(node)) {
        // closing tag — dedent (clamped so malformed input never goes negative)
        pad = Math.max(0, pad - 1);
      } else if (/^<\w[^>]*[^/]>.*$/.test(node)) {
        // opening tag (not self-closing) — indent children
        indent = 1;
      }
      const line = PADDING.repeat(pad) + node;
      pad += indent;
      return line;
    })
    .join("\n");
}

function formatXml(content: string): string {
  const doc = new DOMParser().parseFromString(content, "application/xml");
  const serialized = new XMLSerializer().serializeToString(doc.documentElement);
  return indentXml(serialized);
}

/**
 * Pretty-print `content` according to the detected `format`.
 * Falls back to the raw string if formatting throws, so callers never crash.
 */
export function formatContent(content: string, format: XmlJsonFormat): string {
  try {
    if (format === "json") return formatJson(content);
    if (format === "xml") return formatXml(content);
  } catch {
    // defensive — detection already validated, but never throw to the view
  }
  return content;
}
